/**
 * App DATASETS manager (ADR-0048 single-IDE) — a builder rail BOTTOM-zone area
 * (the app-global zone, next to App Query / Tabelle / Stato globale), where the
 * operator AUTHORS the app's datasets (read-model PROJECTIONS over a base table)
 * WITHOUT leaving the editor. Part of the north-star "app-detail goes away;
 * everything lives in the editor".
 *
 * Follows the WS-7/WS-8 rail-bottom MASTER/DETAIL pattern (mirror of
 * `AppTablesPane`/`AppQueriesPane`): a `view` prop (`"list"|"detail"|"full"`)
 * with CONTROLLED selection lifted to `PageBuilderPage`, so it mounts TWICE —
 * the LIST in the lateral sidebar, the DETAIL in the content. The DETAIL reuses
 * the SHARED `DatasetDesigner` (joins / columns / filters / sort + preview) —
 * the same component the standalone `DatasetEditorPage` route renders, so there's
 * ONE designer. The `["apps", appId, "datasets"]` TanStack query is cache-shared
 * with the builder's data-source pickers, so a create/delete here repopulates
 * them.
 *
 * UNLIKE tables, a dataset needs a BASE TABLE chosen at creation (immutable
 * afterwards), so "＋ Nuova" opens a small dialog (name + base table) instead of
 * an immediate create.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Database, Plus, Trash2 } from "lucide-react";
import * as React from "react";

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { appsApi, type DatasetCreate, type DatasetOut, type TableSummary } from "@/lib/api-apps";
import { DatasetQueryBuilder } from "@/components/datasets/DatasetQueryBuilder";

const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

// Columns a fresh dataset can project (have physical storage). Mirror of the
// backend projectable set + the old CreateDatasetDialog.
const DATASET_PROJECTABLE = new Set(["data", "relation", "relation_many"]);

type AreaView = "list" | "detail" | "full";

export function AppDatasetsPane({
  appId,
  canWrite = true,
  view = "full",
  selectedId: selectedIdProp,
  onSelectId,
}: {
  appId: string;
  canWrite?: boolean;
  view?: AreaView;
  /** Controlled selection (master/detail split). A dataset id or null. Omit for
   *  the legacy single pane. */
  selectedId?: string | null;
  onSelectId?: (id: string | null) => void;
}) {
  const qc = useQueryClient();
  const datasetsQ = useQuery({
    queryKey: ["apps", appId, "datasets"],
    queryFn: () => appsApi.listDatasets(appId),
  });
  const datasets = React.useMemo(() => datasetsQ.data ?? [], [datasetsQ.data]);

  // The base-table picker (create dialog) + the list's base-table labels.
  const tablesQ = useQuery({
    queryKey: ["apps", appId, "tables"],
    queryFn: () => appsApi.listTables(appId),
  });
  const tables = React.useMemo(() => tablesQ.data ?? [], [tablesQ.data]);
  const tableName = (id: string) => tables.find((tb) => tb.id === id)?.name ?? id;

  const controlled = onSelectId !== undefined;
  const [internalId, setInternalId] = React.useState<string | null>(null);
  const selectedId = controlled ? selectedIdProp ?? null : internalId;
  const setSelectedId = (id: string | null) => (controlled ? onSelectId!(id) : setInternalId(id));

  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState("");
  const [createOpen, setCreateOpen] = React.useState(false);

  const visible = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return datasets;
    return datasets.filter((x) => x.name.toLowerCase().includes(q));
  }, [datasets, search]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["apps", appId, "datasets"] });

  const deleteMut = useMutation({
    mutationFn: (id: string) => appsApi.deleteDataset(appId, id),
    onSuccess: () => {
      invalidate();
      setSelectedId(null);
    },
    onError: (e) => setError(errMsg(e)),
  });

  const selected = selectedId ? datasets.find((d) => d.id === selectedId) ?? null : null;

  // --- MASTER (list) ---------------------------------------------------------
  const masterCls =
    view === "full"
      ? "flex w-56 shrink-0 flex-col border-r border-border/60 pr-2"
      : "flex h-full min-h-0 flex-col";

  const master = (
    <div className={masterCls}>
      <div className="mb-2 shrink-0 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Proiezioni
      </div>
      {canWrite && (
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          disabled={tables.length === 0}
          title={tables.length === 0 ? "Crea prima una tabella" : undefined}
          className="mb-1 flex shrink-0 items-center justify-center gap-1.5 rounded-md border border-dashed border-border px-2 py-1.5 text-xs text-muted-foreground hover:border-accent/60 hover:text-foreground disabled:opacity-40"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> Nuova proiezione
        </button>
      )}
      {datasets.length > 5 && (
        <input
          className="mb-1 rounded border border-border bg-background px-1.5 py-1 text-[11px]"
          placeholder="Cerca…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {datasets.length === 0 ? (
          <p className="px-1 py-2 text-[11px] text-muted-foreground">
            {tables.length === 0
              ? "Aggiungi prima una tabella, poi crea una proiezione."
              : "Nessuna proiezione. Creane una con «Nuova»."}
          </p>
        ) : visible.length === 0 ? (
          <p className="px-1 py-2 text-[11px] text-muted-foreground">Nessun risultato.</p>
        ) : (
          visible.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => setSelectedId(d.id)}
              className={`mb-0.5 flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left text-[12px] ${
                selectedId === d.id ? "bg-accent/15 text-accent" : "hover:bg-surface"
              }`}
            >
              <Database
                className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70"
                strokeWidth={1.75}
              />
              <span className="min-w-0 flex-1 truncate font-medium">{d.name}</span>
              <span className="shrink-0 truncate text-[9px] text-muted-foreground">
                {tableName(d.base_table_id)}
              </span>
            </button>
          ))
        )}
      </div>
      {error && (
        <pre className="mt-1 shrink-0 whitespace-pre-wrap break-words rounded border border-danger/40 bg-danger/5 p-1.5 font-mono text-[10px] text-danger">
          {error}
        </pre>
      )}
    </div>
  );

  // --- DETAIL ----------------------------------------------------------------
  const detailInner = !selected ? (
    <div className="flex flex-1 items-center justify-center text-[12px] text-muted-foreground">
      Seleziona una proiezione dalla lista, o creane una nuova.
    </div>
  ) : (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-2 flex shrink-0 items-center gap-2 border-b border-border/50 pb-2">
        <Database className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">{selected.name}</span>
        {canWrite && (
          <button
            type="button"
            onClick={() => {
              if (window.confirm(`Eliminare la proiezione "${selected.name}"?`))
                deleteMut.mutate(selected.id);
            }}
            className="flex items-center gap-1 rounded px-2 py-1 text-[11px] text-danger hover:bg-danger/10"
          >
            <Trash2 className="h-3 w-3" strokeWidth={1.5} /> Elimina
          </button>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <DatasetQueryBuilder appContext={{ appId, datasetId: selected.id, canWrite }} />
      </div>
    </div>
  );

  const createDialog = (
    <CreateDatasetDialog
      appId={appId}
      tables={tables}
      open={createOpen}
      onOpenChange={setCreateOpen}
      onCreated={(ds) => setSelectedId(ds.id)}
    />
  );

  if (view === "list")
    return (
      <>
        {master}
        {createDialog}
      </>
    );
  if (view === "detail")
    return <div className="flex h-full min-h-0 flex-col text-[12px]">{detailInner}</div>;

  return (
    <div className="flex h-full min-h-0 gap-3 text-[12px]">
      {master}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">{detailInner}</div>
      {createDialog}
    </div>
  );
}

/** Create dialog — a dataset needs a base table (immutable later). Seeds a valid
 *  spec (project all projectable base columns, falling back to the id stamp) so
 *  the editor opens on something runnable. Selects the new dataset on success
 *  (no navigation — we live inside the editor). */
function CreateDatasetDialog({
  appId,
  tables,
  open,
  onOpenChange,
  onCreated,
}: {
  appId: string;
  tables: TableSummary[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (ds: DatasetOut) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = React.useState("");
  const [tableId, setTableId] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setName("");
      setTableId(tables[0]?.id ?? "");
    }
  }, [open, tables]);

  const create = useMutation({
    mutationFn: async (body: { name: string; tableId: string }) => {
      const detail = await appsApi.getTable(appId, body.tableId);
      const cols = detail.columns.filter((c) => DATASET_PROJECTABLE.has(c.kind));
      const columns = cols.length
        ? cols.map((c) => ({ key: c.name, source: { alias: "base", column: c.name } }))
        : [{ key: "id", source: { alias: "base", column: "id" } }];
      const payload: DatasetCreate = {
        name: body.name,
        config: {
          base_table: body.tableId,
          joins: [],
          columns,
          filters: [],
          group_by: [],
          aggregates: [],
          sort: [],
        },
      };
      return appsApi.createDataset(appId, payload);
    },
    onSuccess: (ds) => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "datasets"] });
      onOpenChange(false);
      onCreated(ds);
    },
  });

  const canSubmit = name.trim().length > 0 && tableId.length > 0 && !create.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuova proiezione</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ds-name">Proiezione</Label>
            <Input
              id="ds-name"
              value={name}
              autoFocus
              placeholder="Deals + cliente"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ds-table">Tabella base</Label>
            <select
              id="ds-table"
              value={tableId}
              className="block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm"
              onChange={(e) => setTableId(e.target.value)}
            >
              {tables.map((tb) => (
                <option key={tb.id} value={tb.id}>
                  {tb.name}
                </option>
              ))}
            </select>
          </div>
          {create.isError && (
            <Alert tone="danger" title="Impossibile creare la proiezione">
              <span className="font-mono text-xs">{(create.error as Error).message}</span>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button disabled={!canSubmit} onClick={() => create.mutate({ name: name.trim(), tableId })}>
            Crea
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
